import dayjs, { Dayjs, ConfigType } from "dayjs";
import {
	IModel,
	IIdentifiable,
} from "@/loader";

export interface ICapabilities {
	switch_themes?: boolean;
	edit_themes?: boolean;
	activate_plugins?: boolean;
	edit_plugins?: boolean;
	edit_users?: boolean;
	edit_files?: boolean;
	manage_options?: boolean;
	moderate_comments?: boolean;
	manage_categories?: boolean;
	manage_links?: boolean;
	upload_files?: boolean;
	import?: boolean;
	unfiltered_html?: boolean;
	edit_posts?: boolean;
	edit_others_posts?: boolean;
	edit_published_posts?: boolean;
	publish_posts?: boolean;
	edit_pages?: boolean;
	read?: boolean;
	level_10?: boolean;
	level_9?: boolean;
	level_8?: boolean;
	level_7?: boolean;
	level_6?: boolean;
	level_5?: boolean;
	level_4?: boolean;
	level_3?: boolean;
	level_2?: boolean;
	level_1?: boolean;
	level_0?: boolean;
	edit_others_pages?: boolean;
	edit_published_pages?: boolean;
	publish_pages?: boolean;
	delete_pages?: boolean;
	delete_others_pages?: boolean;
	delete_published_pages?: boolean;
	delete_posts?: boolean;
	delete_others_posts?: boolean;
	delete_published_posts?: boolean;
	delete_private_posts?: boolean;
	edit_private_posts?: boolean;
	read_private_posts?: boolean;
	delete_private_pages?: boolean;
	edit_private_pages?: boolean;
	read_private_pages?: boolean;
	delete_users?: boolean;
	create_users?: boolean;
	unfiltered_upload?: boolean;
	edit_dashboard?: boolean;
	update_plugins?: boolean;
	delete_plugins?: boolean;
	install_plugins?: boolean;
	update_themes?: boolean;
	install_themes?: boolean;
	update_core?: boolean;
	list_users?: boolean;
	remove_users?: boolean;
	promote_users?: boolean;
	edit_theme_options?: boolean;
	delete_themes?: boolean;
	export?: boolean;
	restrict_content?: boolean;
	list_roles?: boolean;
	create_roles?: boolean;
	delete_roles?: boolean;
	edit_roles?: boolean;
	delete_blocks?: boolean;
	publish_blocks?: boolean;
	read_private_blocks?: boolean;
	delete_private_blocks?: boolean;
	edit_private_blocks?: boolean;
	manage_laboandco?: boolean;
	[cap: string]: boolean | undefined;
}

export interface IWPUser {
	id?: number;
	ID?: number;
	username?: string;
	name?: string;
	slug?: string;
	nickname?: string;
	email?: string;
	user_status?: number;
	first_name?: string | null;
	last_name?: string | null;
	roles?: string[];
	deleted?: boolean | null;
	edit_url?: string | null;
	description?: string;
	locale?: string;
	capabilities?: ICapabilities;
	extra_capabilities?: ICapabilities;
	registered_date?: ConfigType | null;
}

export interface IWPUserOptions {
	user_login?: boolean;
	user_nicename?: boolean;
	user_email?: boolean;
	user_url?: boolean;
	user_registered?: boolean;
	user_status?: boolean;
	display_name?: boolean;
	first_name?: boolean;
	last_name?: boolean;
	roles?: boolean;
	capabilities: boolean;
	deleted?: boolean;
	edit_url?: boolean;
}

export class WPUser implements IIdentifiable, IWPUser, IModel<IWPUser> {
	public id!: number;
	public username!: string;
	public name!: string;
	public slug!: string;
	public nickname!: string;
	public email!: string;
	public user_status!: number;
	public first_name!: string | null;
	public last_name!: string | null;
	public roles!: string[];
	public deleted!: boolean | null;
	public edit_url!: string | null;
	public description!: string;
	public locale!: string;
	public capabilities!: ICapabilities;
	public extra_capabilities!: ICapabilities;
	public registered_date!: Dayjs | null;

	public get uniqId(): string {
		return "user" + this.id;
	}

	public get firstname(): string {
		return this.first_name || "";
	}

	public get lastname(): string {
		return this.last_name || "";
	}

	public get fullname() {
		return this.name;
	}

	public get isAdmin(): boolean {
		return this.roles.includes("administrator");
	}

	public get isCustomer(): boolean {
		return this.roles.includes("customer");
	}

	public constructor(data: IWPUser = {}) {
		this.load(data);
	}

	public load(data: IWPUser) {
		this.id = Number.orDefault(data.id || data.ID);
		this.username = String.orDefault(data.username);
		this.name = String.orDefault(data.name);
		this.slug = String.orDefault(data.slug);
		this.nickname = String.orDefault(data.nickname);
		this.email = String.orDefault(data.email);
		this.user_status = Number.orDefault(data.user_status);
		this.first_name = String.nullable(data.first_name);
		this.last_name = String.nullable(data.last_name);
		this.roles = data.roles ? data.roles.slice() : [];
		this.deleted = Boolean.orDefault(data.deleted);
		this.edit_url = String.nullable(data.edit_url);
		this.description = String.orDefault(data.description);
		this.locale = String.orDefault(data.locale);
		this.capabilities = Object.assign({}, data.capabilities);
		this.extra_capabilities = Object.assign({}, data.extra_capabilities);
		this.registered_date = dayjs.nullable(data.registered_date);
	}

	public update(data: IWPUser) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.ID !== undefined) {
			this.id = Number.orDefault(data.ID);
		}
		if (data.username !== undefined) {
			this.username = String.orDefault(data.username);
		}
		if (data.name !== undefined) {
			this.name = String.orDefault(data.name);
		}
		if (data.slug !== undefined) {
			this.slug = String.orDefault(data.slug);
		}
		if (data.nickname !== undefined) {
			this.nickname = String.orDefault(data.nickname);
		}
		if (data.email !== undefined) {
			this.email = String.orDefault(data.email);
		}
		if (data.user_status !== undefined) {
			this.user_status = Number.orDefault(data.user_status);
		}
		if (data.first_name !== undefined) {
			this.first_name = String.nullable(data.first_name);
		}
		if (data.last_name !== undefined) {
			this.last_name = String.nullable(data.last_name);
		}
		if (data.roles !== undefined) {
			this.roles = data.roles ? data.roles.slice() : [];
		}
		if (data.deleted !== undefined) {
			this.deleted = Boolean.orDefault(data.deleted);
		}
		if (data.edit_url !== undefined) {
			this.edit_url = String.nullable(data.edit_url);
		}
		if (data.description !== undefined) {
			this.description = String.orDefault(data.description);
		}
		if (data.locale !== undefined) {
			this.locale = String.orDefault(data.locale);
		}
		if (data.capabilities !== undefined) {
			this.capabilities = Object.assign({}, data.capabilities);
		}
		if (data.extra_capabilities !== undefined) {
			this.extra_capabilities = Object.assign({}, data.extra_capabilities);
		}
		if (data.registered_date !== undefined) {
			this.registered_date = dayjs.nullable(data.registered_date);
		}
	}
}
